import { useCallback, useEffect, useState } from 'react'
import { throttle } from 'lodash-es'
import { useObservableValue } from '@/hooks/rx-hooks'
import { mediaQueryMatchStream } from '@/utils/browser-util'

/**
 * Hook that returns the viewport size and listens for changes.
 */
export function useViewportSize() {
  const [viewport, setViewport] = useState(() => readViewportSize())
  const updateViewport = useCallback(() => setViewport(readViewportSize()), [])
  useEffect(() => {
    const handler = throttle(updateViewport, 400)
    window.addEventListener('resize', handler)
    return () => {
      window.removeEventListener('resize', handler)
    }
  }, [updateViewport])
  return viewport
}

function readViewportSize() {
  const width = Math.max(
    document.documentElement.clientWidth,
    window.innerWidth || 0,
  )
  const height = Math.max(
    document.documentElement.clientHeight,
    window.innerHeight || 0,
  )
  return { width, height }
}

// NOTE: This is mirrored in `css/media.css`
export const isMobile$ = mediaQueryMatchStream('screen and (max-width: 500px)')

/**
 * Checks whether the viewport is phone-sized.
 *
 * Media query is from `css/media.css`.
 */
export function useIsMobile() {
  return useObservableValue(isMobile$)
}

// NOTE: This is mirrored in `css/media.css`
export const isRailCollapsed$ = mediaQueryMatchStream('(max-width: 1279px)')

/**
 * Checks whether the navigation rail is collapsed to icons.
 *
 * Media query is from `css/media.css`.
 */
export function useIsRailCollapsed() {
  return useObservableValue(isRailCollapsed$)
}

// NOTE: This is mirrored in `css/media.css`
export const isSidebarCollapsed$ = mediaQueryMatchStream('(width <= 1200px)')

/**
 * Checks whether the job and notification sidebars are collapsed.
 *
 * Media query is from `css/media.css`.
 */
export function useIsSidebarCollapsed() {
  return useObservableValue(isSidebarCollapsed$)
}

// NOTE: This is mirrored in `css/media.css`
export const isSideSheetAbove$ = mediaQueryMatchStream('(width < 1200px)')

/**
 * Checks whether side sheets sit above the content instead of beside it.
 * Unlike the sidebars, they stay beside the content at exactly 1200px.
 *
 * Media query is from `css/media.css`.
 */
export function useIsSideSheetAbove() {
  return useObservableValue(isSideSheetAbove$)
}

// NOTE: This is mirrored in `css/media.css`
/**
 * Whether the viewport is too narrow to keep the job list open beside a job.
 */
export const isJobListAutoHidden$ = mediaQueryMatchStream('(width < 1600px)')
